import { AITool } from "@/comps/editors/deform"
import clsx from "clsx"
import {
	AiStudioPageType,
	ChatController,
	ChatPageType,
	CompletedMessageProps,
	DiscoverPageType,
	HomePageType,
	PageName,
} from "controllers/dashboard"
import {
	DynamicEditorProps,
	EditorsController,
} from "controllers/editors"
import dynamic from "next/dynamic"
import { StartSessionWithInputsRequest } from "pages/api/wizard-start-session-with-inputs"
import { RefObject } from "react"
import { SessionItemGroup } from "sections/dashboard/video-gpt"
import { AdExamplesPopupProps } from "sections/gallery/ad-examples-popup"
import { Pages } from "sections/gallery/all-gallery"
import { GalleryPopupProps } from "sections/gallery/gallery-popup"
import { HistoryPopupProps } from "sections/history/history-popup"
import styled, { keyframes } from "styled-components"
import { EditSegmentsAction } from "../wizard/response-message"
import { PopupsProps } from "./popups"

const runnerAnim = keyframes`
	0% {
    transform: translateX(-100%);
  }
  20% {
    transform: translateX(0%);
  }
  70% {
    transform: translateX(100%);
  }
    100% {
    transform: translateX(200%);
  }
`

const RunnerContainer = styled.div`
	position: absolute;
	top: 0;
	width: 70%;
	height: 100%;
	background-color: var(--color-primary-500);

	animation: ${runnerAnim} 1s linear infinite;
`

const LoadingRunner = () => {
	return (
		<div className="pointer-events-none fixed left-0 top-0 z-[999] flex h-screen w-screen items-start justify-start bg-color-popup">
			<div className="relative h-1 w-full overflow-hidden">
				<RunnerContainer />
			</div>
		</div>
	)
}

const Home = dynamic(
	() => import("sections/dashboard/home"),
	{
		loading: () => <LoadingRunner />,
		ssr: false,
	},
)

const AiStudio = dynamic(
	() => import("sections/dashboard/ai-studio"),
	{
		loading: () => <LoadingRunner />,
		ssr: false,
	},
)

const Discover = dynamic(
	() => import("sections/dashboard/discover"),
	{
		loading: () => <LoadingRunner />,
		ssr: false,
	},
)

const VideoGpt = dynamic(
	() => import("sections/dashboard/video-gpt"),
	{
		loading: () => <LoadingRunner />,
		ssr: false,
	},
)

interface DashboardContentProps {
	setHistoryPopupState: (
		history: HistoryPopupProps | null,
	) => void
	loadMoreHistoryContent: (
		offset: number,
	) => Promise<number[]>
	loadMoreSessions: (offset: number) => Promise<number[]>
	generateVideo: (message: string) => Promise<void>
	changeProjectsDisplay: (
		display: "column" | "grid",
	) => void
	changeDiscoverFilter: (filter: Pages) => void
	setGalleryPopupState: (
		gallery: GalleryPopupProps | null,
	) => void
	renameSession: (
		id: number,
		newName: string,
	) => Promise<void>
	setStartSessionInputs: (
		data: StartSessionWithInputsRequest,
	) => void
	deleteSession: (id: number) => void
	setPopupsProps: (v: PopupsProps | null) => void
	duplicateSession: (id: number) => Promise<void>
	remakeSession: (id: number) => Promise<void>
	deleteHistoryItem: (id: number) => Promise<void>
	setSubscriptionPopupOpen: (v: boolean) => void
	setChangeName: (a: SessionItemGroup) => void
	setDeleteSessionPopup: (a: number | null) => void
	setCompletedMessage: (
		v: CompletedMessageProps | null,
	) => void
	setSelectedSession: (id: number) => void
	changeSelectedPage: (name: PageName) => Promise<void>
	setAdExamplesPopupState: (
		a: AdExamplesPopupProps | null,
	) => void
	startNewSession: () => void
	changeActiveChild: (v: string | null) => void
	setIndexedPayload: (a: DynamicEditorProps | null) => void
	openUploadStockPopup: (name: EditSegmentsAction) => void
	selectCreatorTool: (
		tool: AITool,
		content: DynamicEditorProps,
		changePage?: boolean,
	) => Promise<void>
	deleteSessionPopup: number | null
	historyPopupState: HistoryPopupProps | null
	canTryAgain: boolean
	activePage: PageName
	videoGpt: HomePageType
	home: AiStudioPageType
	chat: ChatPageType
	discover: DiscoverPageType
	chatController: ChatController
	textAreaRef: RefObject<HTMLDivElement>
	changeName: SessionItemGroup
	editors: EditorsController
}

export function DashboardContent(
	props: DashboardContentProps,
) {
	const {
		setSubscriptionPopupOpen,
		setAdExamplesPopupState,
		loadMoreHistoryContent,
		changeProjectsDisplay,
		changeDiscoverFilter,
		setHistoryPopupState,
		setGalleryPopupState,
		setStartSessionInputs,
		setDeleteSessionPopup,
		openUploadStockPopup,
		changeSelectedPage,
		setSelectedSession,
		setCompletedMessage,
		selectCreatorTool,
		deleteHistoryItem,
		changeActiveChild,
		setIndexedPayload,
		duplicateSession,
		loadMoreSessions,
		setPopupsProps,
		remakeSession,
		renameSession,
		deleteSession,
		generateVideo,
		setChangeName,
		startNewSession,
		canTryAgain,
		deleteSessionPopup,
		historyPopupState,
		textAreaRef,
		activePage,
		changeName,
		editors,
		discover,
		chat,
		home,
		videoGpt,
		chatController,
	} = props

	switch (activePage) {
		case "home":
			return (
				<AiStudio
					page={home}
					deleteHistoryItem={deleteHistoryItem}
					setHistoryPopupState={setHistoryPopupState}
					loadMoreHistoryContent={loadMoreHistoryContent}
					selectCreatorTool={selectCreatorTool}
					setSubscriptionPopupOpen={
						setSubscriptionPopupOpen
					}
					changeSelectedPage={changeSelectedPage}
					changeActiveChild={changeActiveChild}
					historyPopupState={historyPopupState}
					editors={editors}
					setIndexedPayload={setIndexedPayload}
					canTryAgain={canTryAgain}
				/>
			)

		case "videoGpt":
			return (
				<Home
					page={videoGpt}
					changeActiveChild={changeActiveChild}
					setPopupsProps={setPopupsProps}
					generateVideo={generateVideo}
					deleteSession={deleteSession}
					loadMoreSessions={loadMoreSessions}
					changeProjectsDisplay={changeProjectsDisplay}
					renameSession={renameSession}
					duplicateSession={duplicateSession}
					remakeSession={remakeSession}
					setStartSessionInputs={setStartSessionInputs}
					setSelectedSession={setSelectedSession}
					startNewSession={startNewSession}
					setSubscriptionPopupOpen={
						setSubscriptionPopupOpen
					}
				/>
			)

		case "discover":
			return (
				<Discover
					page={discover}
					changeDiscoverFilter={changeDiscoverFilter}
					setGalleryPopupState={setGalleryPopupState}
					setAdExamplesPopupState={setAdExamplesPopupState}
					changeActiveChild={changeActiveChild}
					selectCreatorTool={selectCreatorTool}
					startNewSessionWithMessage={generateVideo}
				/>
			)

		case "chat":
			return (
				<VideoGpt
					chat={chat}
					setSubscriptionPopupOpen={
						setSubscriptionPopupOpen
					}
					chatController={chatController}
					setChangeName={setChangeName}
					changeName={changeName}
					textAreaRef={textAreaRef}
					setDeleteSessionPopup={setDeleteSessionPopup}
					openUploadStockPopup={openUploadStockPopup}
					deleteSessionPopup={deleteSessionPopup}
					setCompletedMessage={setCompletedMessage}
				/>
			)
	}
}

export function ContentContainer(props: {
	children: JSX.Element | JSX.Element[]
	padding: boolean
	id?: string
	onClick?: (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>,
	) => void
	parentRef?: RefObject<HTMLDivElement>
	withoutHeight?: boolean
}) {
	const {
		children,
		padding,
		id,
		onClick,
		parentRef,
		withoutHeight,
	} = props

	return (
		<div
			ref={parentRef}
			id={id}
			onClick={onClick}
			className={clsx(
				padding && "p-[12px] desktop:p-[30px]",
				!withoutHeight &&
					"h-[calc(100svh-58px)] tablet:h-[calc(100vh-70px)]",
				"no-scrollbar flex flex-col gap-[24px] scroll-smooth",
				"w-full overflow-x-hidden overflow-y-scroll bg-color-dashboard-bg",
			)}
			style={{
				overflowY: "scroll",
			}}>
			{children}
		</div>
	)
}
